<template>
  <div ref="dpComponent">
    <input
      :value="displayDate"
      placeholder="DD/MM/AAAA"
      class="box-border w-full rounded-md border-2 border-black bg-transparent p-2 text-sm placeholder-neutral-500"
      @click="visible = true"
      :disabled="disabled"
    />
    <div v-if="visible" class="relative w-full">
      <div
        class="absolute top-2 flex w-full overflow-hidden rounded-lg border-2 border-solid border-neutral-900 bg-white"
      >
        <ul class="datepicker-col">
          <li v-for="d in 31" :key="d" :class="{ active: isCurrentDay(d) }" @click="selectDay(d)">{{ d }}</li>
        </ul>
        <ul class="datepicker-col">
          <li v-for="m in 12" :key="m" :class="{ active: isCurrentMonth(m) }" @click="selectMonth(m)">{{ m }}</li>
        </ul>
        <ul class="datepicker-col">
          <li v-for="y in yearRange" :key="y" :class="{ active: isCurrentYear(y) }" @click="selectYear(y)">{{ y }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref, watch } from 'vue'

export default defineComponent({
  props: {
    startingYear: Number,
    endingYear: Number,
    modelValue: String,
    disabled: Boolean
  },
  emits: ['OnChange'],
  setup(props, { emit }) {
    // DatepickerCompoent: armazena informações relacionadas a click exterior
    const dpComponent = ref(null)
    const visible = ref(false)

    const current = reactive({
      day: 0,
      month: 0,
      year: 0
    })

    watch(
      () => props.modelValue,
      (value) => {
        if (value) {
          let d = new Date(value)
          current.day = d.getDate()
          current.month = d.getMonth()
          current.year = d.getFullYear()
        }
      }
    )

    // @TODO: Adicionar teste unitário para a lógica abaixo
    // "Para facilitar os testes, pode-se extrair a lógica do componente para um helper lá na pasta src/common"
    const displayDate = computed(() => {
      return [
        current.day ? `${current.day}`.padStart(2, '0') : 'DD',
        current.month ? `${current.month}`.padStart(2, '0') : 'MM',
        current.year || 'AAAA'
      ].join('/')
    })

    // @TODO: Definir padrões para startingYear e endingYear (quando o usuário não informa)
    const yearRange = computed(() => {
      const startingYear = props.startingYear || new Date().getFullYear()
      const endingYear = props.endingYear || startingYear + 10

      const range = []
      for (let year = startingYear; year <= endingYear; year++) range.push(year)

      return range
    })

    const isCurrentDay = (day) => day === current.day
    const isCurrentMonth = (month) => month === current.month
    const isCurrentYear = (year) => year === current.year

    /**
     * Auto-esconde o componente quando o usuário clica fora do elemento
     * @param event
     */
    const clickOutside = (e) => {
      if (!dpComponent.value.contains(e.target)) {
        visible.value = false
      }
    }

    onMounted(() => document.addEventListener('click', clickOutside, false))
    onUnmounted(() => document.removeEventListener('click', clickOutside, false))

    const selectDay = (d) => {
      current.day = d
      emit('OnChange', current)
    }

    const selectMonth = (m) => {
      current.month = m
      emit('OnChange', current)
    }

    const selectYear = (y) => {
      current.year = y
      emit('OnChange', current)
    }

    return {
      dpComponent,
      current,
      visible,
      displayDate,
      yearRange,
      isCurrentDay,
      isCurrentMonth,
      isCurrentYear,
      selectDay,
      selectMonth,
      selectYear
    }
  }
})
</script>

<style lang="scss" scoped>
.datepicker-col {
  @apply max-h-40 grow;
  overflow-y: scroll;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  li {
    padding: 4px 12px;
    display: block;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      background: #ccc;
    }

    &.active {
      @apply bg-green-300;
    }
  }
}
</style>
